<template>
  <div class="vehicles">
    <HeaderParticipant 
        :participants="participants" 
        :participant="participant" 
        @selectParticipants="selectParticipants" 
        @changeSelect="changeSelect"
    />
    <v-row :class="{ 'mt-4': !age18 }">
      <v-col cols="12" md="12" sm="12">
        <v-dialog v-model="dialog" max-width="600px">
          <v-card>
            <v-form ref="form">
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text>
                <v-row no-gutters>
                  <v-col md="12" class="mt-2" cols="12">
                    <v-text-field
                      v-model="form.year"
                      :label="$t('vehicles.year')"
                      :rules="rules.year"
                      type="number"
                      outlined dense
                    ></v-text-field>
                  </v-col>
                  <v-col md="12" cols="12">
                    <v-select
                      v-model="form.vehicleTypeId"
                      :label="$t('vehicles.vehicle_type')"
                      :rules="rules.vehicle_type"
                      :items="vehicleTypeItems"
                      item-text="vehicleTypeName"
                      item-value="vehicleTypeId"
                      @change="getBrandsByType"
                      outlined dense
                      :no-data-text="$t('common.no_data')"
                    ></v-select>
                  </v-col>
                  <v-col md="12" cols="12">
                    <v-select
                      v-model="form.brand"
                      :label="$t('vehicles.brand')"
                      :rules="rules.brand"
                      :items="brandItems"
                      item-text="brandName"
                      item-value="brandName"
                      outlined dense
                      :no-data-text="$t('common.no_data')"
                    ></v-select>
                  </v-col>
                  <v-col md="12" cols="12">
                    <v-text-field
                      v-model="form.model"
                      :label="$t('vehicles.model')"
                      :rules="rules.model"
                      outlined dense
                    ></v-text-field>
                  </v-col>
                  <v-col md="12" cols="12">
                    <v-text-field
                      v-model="form.size"
                      :label="$t('vehicles.size')"
                      :rules="rules.size"
                      outlined dense
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  @click="dialog = false"
                  :color="`${buttonColor}`"
                  class="btn mb-2 mr-2"
                >{{ $t('vehicles.cancel') }}</v-btn>
                <v-btn
                  @click="createVehicle"
                  :color="`${buttonColor}`"
                  class="btn mb-2"
                  :disabled="!vehicleFormIsValid"
                >{{ $t('vehicles.save') }}</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="600px">
          <v-card>
            <v-card-title class="headline">{{ $t('vehicles.delete_vehicle') }}</v-card-title>
            <v-card-text>
              <p class="mt16">{{ $t('vehicles.sure_delete') }}</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
                <v-btn class="btn mb-2 mr-2" :color="`${buttonColor}`" @click="closeDelete">{{ $t('vehicles.cancel') }}</v-btn>
                <v-btn
                  :color="`${buttonColor}`"
                  class="btn mb-2"
                  @click="deleteItemConfirm"
                >{{ $t('vehicles.delete') }}</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <div v-for="part in participantList" :key="part.id" class="ml-2 mr-2 mb-2">
          <v-card class="elevation-4" :style="`background:${cardBGColor}`">
            <v-card-text>
              <v-row no-gutters>
                <v-col md="6" class="text-left" style="position:relative">
                  <HeaderCard :part="part" />
                </v-col>
                <v-col md="6" class="text-right">
                  <v-btn
                    :color="`${buttonColor}`"
                    dark
                    @click="openDialogCreate(part.userId)"
                  >{{ $t("vehicles.create_vehicle") }}</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col md="12">
                  <v-data-table
                    :headers="vehicleHeaders"
                    :items="part.vehicles"
                    sort-by="year"
                    class="elevation-1"
                    :no-data-text="$t('vehicles.no_data_available')"
                    :footer-props="{
                      'items-per-page-text': $t('vehicles.items_per_page_text'),
                      'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
                    }" 
                    dense
                    :headerProps="{
                      'sort-by-text': $t('dataTable.sortByText')
                    }"
                  >
                    <template v-slot:[`item.vehicleType`]="{ item }">
                      {{ item.vehicleType[locale] ? item.vehicleType[locale] : item.vehicleType[defaultLocale] }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-icon small class="mr-2" @click="editItem(item)">fa far fa-pencil-alt</v-icon>
                      <v-icon small @click="deleteItem(item)">fa far fa-trash-alt</v-icon>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <Snack :snack="snackbar" :snackMsg="msg_snackbar" :snackCol="color_snackbar"/>
  </div>
</template>

<script>
import VehicleService from "../services/VehicleService";
import AuthService from "../services/AuthService";
import Snack from "../components/notifications/Snack";
import { VCard, VCol, VRow, VBtn, VIcon, VSelect, VTextField, VCardText, VForm, VDialog, VDataTable, 
         VCardTitle, VCardActions, VSpacer } from 'vuetify/lib';
import GeneralService from "@/services/GeneralService";
import HeaderCard from "@/components/HeaderCard";
import { mapState } from "vuex";
import HeaderParticipant from "@/components/HeaderParticipant";

export default {
  name: "Vehicles",
  components:{
    Snack,
    VCard, VCol, VRow, VBtn, VIcon, VSelect, VTextField, VCardText, VForm, VDialog, VDataTable, 
    VCardTitle, VCardActions, VSpacer, 
    HeaderCard, HeaderParticipant
  },
  data() {
    const vehicleForm = Object.freeze({
      year: 0,
      vehicle_type: "",
      brand: "",
      model: "",
      size: "",
      userId: 0
    });
    return {
      color_snackbar: "",
      msg_snackbar: "",
      snackbar: false,
      dialog: false,
      editedIndex: -1,
      dialogDelete: false,
      vehicleForm,
      form: Object.assign({}, vehicleForm),
      vehicleItems: [],
      defaultItem: {
        year: "",
        vehicle_type: "",
        brand: "",
        model: "",
        size: ""
      },
      brandItems: [],
      vehicleTypeItems: [],
      vehicleBrand: [],
      userId: 0,
      buttonColor: "#000",
      cardBGColor: "#f1f1f1",
      bandLoadingPartSelected: false,
      participantList: []
    };
  },
  created(){
    this.buttonColor = this.$store.getters.env.get('VUE_APP_BUTTON_COLOR');
    this.cardBGColor = this.$store.getters.env.get('VUE_APP_BACKGROUND_CARD_PART');
    this.borderAlertColor = this.$store.getters.env.get('VUE_APP_BORDER_ALERT_REGULAR');
    this.iconAlertColor = this.$store.getters.env.get('VUE_APP_ICON_ALERT_REGULAR');
    this.bgAlertColor = this.$store.getters.env.get('VUE_APP_BG_ALERT_REGULAR');
    this.getParticipants();
    this.getVehiclesType();
  },
  mounted() {
    this.$emit("titlePage", "vehicles.title");
    this.$emit("dynamicMenuItems", this.dynamicMenuItems);
  },
  computed: {
    vehicleFormIsValid() {
      return (
        this.form.year &&
        this.form.brand &&
        this.form.vehicleTypeId &&
        this.form.model &&
        this.form.size
      );
    },
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("vehicles.create_vehicle")
        : this.$t("vehicles.edit_vehicle");
    },
    btnTitle() {
      return this.editedIndex === -1
        ? this.$t("vehicles.create")
        : this.$t("vehicles.edit");
    },
    vehicleHeaders() {
      return [
        {
          text: this.$t("vehicles.year"),
          align: "",
          value: "year"
        },
        { text: this.$t("vehicles.vehicle_type"), value: "vehicleType"},
        { text: this.$t("vehicles.brand"), value: "brand" },
        { text: this.$t("vehicles.model"), value: "model" },
        { text: this.$t("vehicles.size"), value: "size" },
        {
          text: this.$t("vehicles.actions"),
          value: "actions",
          sortable: false
        }
      ];
    },
    ...mapState({
      content2(state) {
        return this.$t("common.selectParticipants.content_2", { livelapsId: state.session.userid })
      },
      age18: (state) => state.session.age18,
      userStore: (state) => state.session.user,
      participants: (state) => state.session.participants,
      participant: (state) => state.session.participantsSelected
    }),
    locale() { return this.$i18n.locale; },
    defaultLocale() { return this.$i18n.fallbackLocale; },
    rules() {
      return {
        year: [
          val => ((val || '').toString().length > 0 && val != 0) || this.$t("vehicles.year_required"),
          val => val > 0 || this.$t("vehicles.year_invalid")
        ],
        brand: [
          val => (val || "").length > 0 || this.$t("vehicles.brand_required")
        ],
        vehicle_type: [
          val => val > 0 || this.$t("vehicles.vehicle_type_required")
        ],
        model: [
          val => (val || "").length > 0 || this.$t("vehicles.model_required")
        ],
        size: [
          val => (val || "").length > 0 || this.$t("vehicles.size_required")
        ]
      };
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    userStore(val) {
      if(val) {
        this.getParticipants();
      }
    },
    participant() {
      if(!this.bandLoadingPartSelected) {
        this.bandLoadingPartSelected = true;
        this.selectParticipants();
      }
    }
  },
  methods: {
    loadImage(url) {
        const urlImage = GeneralService.cleanImage(url, this.$store.getters.env.get('VUE_APP_IMAGE_PLACEHOLDER'));
        return urlImage;
    },
    selectParticipants(){
      this.participantList = this.participantList.filter((el) => {
        const find = this.participant.find((ele) => { 
          return ele.userId ? el.userId == ele.userId : el.id == ele.id
        });
        return find ? true : false;
      });
      const usersId = this.participant.map((el) => { return el.userId; });
      this.getVehiclesUsers(usersId);
      AuthService.setAuthParticipants(this.userStore.id, this.participant); 
    },
    async getParticipants(){
      if(!this.bandLoadingParticipants) {
        if(this.userStore) {
          this.bandLoadingParticipants = true;
          this.$store.dispatch('setParticipantsAction');  
        } else {
          this.$store.dispatch('setUserAction');
        }
      }
    },
    showSnackBar(msg, color){
      this.snackbar = true;
      this.msg_snackbar = msg;
      this.color_snackbar = color;
      setTimeout(() => this.snackbar = false, 3000);
    },
    getBrandsByType() {
      VehicleService.getBrandsByType(this.form.vehicleTypeId).then(response => {
        if (response.data.success == 1) {
          this.brandItems = response.data.message;
        }
      });
    },
    getVehiclesType() {
      VehicleService.getTypesVehicle().then(response => {
        if (response.data.success == 1) {
          this.vehicleTypeItems = response.data.message;
        }
      });
    },
    getVehicleType(id){
      let _type = "";
      this.vehicleTypeItems.forEach(type =>{
        if(type.vehicleTypeId == id) {
         _type = type.vehicleTypeName;
        }
      });
      return _type;
    },
    getVehicles(userId) {
      let vehicles = [];
      VehicleService.getAll(userId).then(response => {
        if (response.data.success == 1) {
          response.data.message.map(function(value) {
            vehicles.push({
              id: value.vehicleId,
              brand: value.brand,
              model: value.model,
              size: value.size,
              vehicleTypeId: value.vehicleTypeId,
              year: value.year,
              userId: value.userId
            });
          });
          
          vehicles.forEach(vehicle =>{
            vehicle.type = this.getVehicleType(vehicle.vehicleTypeId);
          });
        }
      });
      this.vehicleItems = vehicles;
    },
    getVehiclesUsers(usersId) {
      VehicleService.getVehiclesUsers({ usersId }).then(response => {
        if(response.data.success == 1) {
          this.participantList = this.participant.map((el) => {
            const filter = response.data.message.filter((el1) => el.userId == el1.userId);
            return {
              ...el, 
              vehicles: filter.map((value) => {
                return {
                  id: value.vehicleId,
                  brand: value.brand,
                  model: value.model,
                  size: value.size,
                  vehicleTypeId: value.vehicleTypeId,
                  year: value.year,
                  userId: value.userId,
                  vehicleType: value.vehicleType
                };
              })
            }
          }); 
        }
      });
      
    },
    setVehicle(data) {
      VehicleService.create(data).then(response => {
        if (response.data.success == 1) {
          this.contactItems = [];
          this.selectParticipants();
          this.showSnackBar(this.$t("vehicles.msg_success_created"), "success");
        }else{
          this.showSnackBar(this.$t("vehicles."+response.data.message), "error");
        }
      });
    },
    deleteVehicle(id) {
      VehicleService.delete(this.form.userId, id).then(response => {
        if (response.data.success == 1) {
          this.vehicleItems = [];
          this.selectParticipants();
          this.showSnackBar(this.$t("vehicles.msg_success_deleted"), "success");
        }else{
          this.showSnackBar(this.$t("vehicles."+response.data.message), "error");
        }
      });
    },
    editVehicle(data) {
      VehicleService.update(data).then(response => {
        if (response.data.success == 1) {
          this.vehicleItems = [];
          this.selectParticipants();
          this.showSnackBar(this.$t("vehicles.msg_success_edited"), "success");
        }else{
          this.showSnackBar(this.$t("vehicles."+response.data.message), "error");
        }
      });
    },
    editItem(item) {
      this.editedIndex = item.id;
      this.form = Object.assign({}, item);
      if(this.$refs.form)
        this.$refs.form.resetValidation();
      this.getBrandsByType();
      this.dialog = true;
    },
    deleteItem(item) {
      this.editedIndex = item.id;
      this.form = Object.assign({}, item);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      this.deleteVehicle(this.form.id);
      this.closeDelete();
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.form = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.form = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    createVehicle() {

      if (this.editedIndex > -1) {
        this.editVehicle(this.form);
      } else {
        this.setVehicle(this.form);
      }
      this.close();
    },
    openDialogCreate(userId){
      this.form.userId = userId;
      if(this.$refs.form)
        this.$refs.form.resetValidation();
      this.dialog = true;
    },
    changeSelect(selectParts) {
        this.$store.commit('setParticipantsSelected', selectParts);
    }
  }
};
</script>

<style>
.vehicles .v-data-table-header,
.vehicles .v-card__title {
  background: #ccc;
}
</style>
<style scoped>
.mt16 {
  margin-top: 16px;
}
.btn{
  color: #fff !important;
}
.partImg{
  width: 40px;
  border-radius: 50%;
  height: 40px;
}
.partSpan{
  color: #000;
  position: absolute;
  top: 10px;
  left: 60px;
}
.v-card__actions{
  display: inherit;
  text-align: right;
}
</style>